import React, { useCallback, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  useCreateTransactionTypeMutation,
  useFetchSingleTransactionTypeQuery,
  useUpdateTransactionTypeMutation,
} from '../transactiontype-api';
import { PageHeader } from '@ant-design/pro-layout';
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Form,
  Input,
  message,
  Select,
  Space,
  Spin,
  Tooltip,
} from 'antd';
import Title from 'antd/lib/typography/Title';
import { HomeOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { TransactionTypeViewModel } from '../../api/models';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { ConvertUTCtoCDT } from '../../../core/utils';
import { getLink } from '../../../App-router';
import { DefaultTenantId } from '../../../core/defaults';
const { Option } = Select;

const isFetchBaseQueryErrorType = (error: any): error is FetchBaseQueryError =>
  error ? 'status' in error : false;

const getErrorMessage = (error: any): string | undefined => error.data;

interface TransactionTypeScreenParams {
Id?: string | undefined;
tenantId?: string | undefined;
  [key: string]: string | undefined;
}

export default function TransactionTypeEditScreen(props: any) {
  const { Id, tenantId = DefaultTenantId}: TransactionTypeScreenParams =
    useParams<TransactionTypeScreenParams>();

  const isNewTransactionType = Id === 'new';

  const { data, isFetching } = useFetchSingleTransactionTypeQuery(
    {
      Id: Number(Id),
    },
    { skip: isNewTransactionType }
  );

  const [createTransactionType, { isLoading: isCreating, error: createError }] = useCreateTransactionTypeMutation();
  const [updateTransactionType, { isLoading: isUpdating, error: updateError }] = useUpdateTransactionTypeMutation();

  const navigate = useNavigate();
  const goBack = useCallback(() => navigate(-1), [navigate]);

  const [form] = Form.useForm();

  React.useEffect(() => {
    form.resetFields();
  }, [data, form]);

  const onFinish = (values: any | TransactionTypeViewModel) => {
    const requestData = {
      transactiontype: {
        id: isNewTransactionType ? 0 : Number(Id),
        name: values.name,
        description: values.description,
        updatedBy:sessionStorage.getItem('userEmail')||'',
        enteredBy:isNewTransactionType?sessionStorage.getItem('userEmail')||'':'',
      },
    };
    const request = isNewTransactionType ? createTransactionType(requestData) : updateTransactionType(requestData);
    request.unwrap().then((response) => {
      if(response?.id ===-200){
        message.error(
          `This combination already exists. Please try other values.`
        );
        return;
      }
      message.success(
        `Transaction Type is ${isNewTransactionType ? 'created' : 'saved'}`
      );
      navigate(
        getLink('TransactionTypeList', {
          ...props,
          tenantId: tenantId,
        })
      );
    });
  };

  const layout = {
    labelCol: { span: 4 },
    wrapperCol: { span: 10 },
  };
  const [disabledSave, setDisabledSave] = useState(true);
  
  const handleFormChange = () => {
  const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
  setDisabledSave(hasErrors);
  }
  const handleCancel = () => {
    navigate(
      getLink('TransactionTypeList', {
        ...props,
        tenantId: tenantId,
      })
    );
  };

  return (
    <div>
      <PageHeader
        breadcrumb={
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to={getLink('home', { tenantId: tenantId })}>
              <HomeOutlined />
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>              
              <Link to={getLink('cob', { tenantId: tenantId })}>
              Dashboard
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={getLink('TransactionTypeList', { ...props, tenantId })}>Transaction Types</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>{isNewTransactionType ? 'New Transaction Type' : data?.name}</Breadcrumb.Item>
          </Breadcrumb>
        }
      >
        {isFetchBaseQueryErrorType(createError) && (
          <Alert message="Error" description={getErrorMessage(createError)} type="error" showIcon />
        )}
        {isFetchBaseQueryErrorType(updateError) && (
          <Alert message="Error" description={getErrorMessage(updateError)} type="error" showIcon />
        )}
        <Card
          title={
            <Title
              level={4}
              style={{
                marginBottom: 0,
                display: 'inline-block',
              }}
            >
              {isNewTransactionType ? 'New Transaction Type' : 'Edit Transaction Type'}
            </Title>
          }
        >
          <Spin spinning={isFetching || isCreating || isUpdating}>
            <Form
              {...layout}
              form={form}
              name="control-hooks"
              initialValues={data}
              onFinish={onFinish}
              onFieldsChange={handleFormChange}   
            >
              <Form.Item name="name" label="Name" rules={[{ required: true }]}>
                <Input placeholder='Please enter valid transaction set identifier like 204, 211 etc.' data-testid="name" type="number" min="0" maxLength={100} suffix={
                        <Tooltip title="Please enter valid transaction set identifier like 204, 211 etc.">
                         <QuestionCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                        </Tooltip>
                      }/>
              </Form.Item>

              <Form.Item name="description" label="Description" rules={[{ required: true }]}>
                <Input placeholder='Please enter valid description like Motor Carrier Load Tender for 210 etc.' showCount data-testid="description" maxLength={1000} suffix={
                        <Tooltip title="Please enter valid description like Motor Carrier Load Tender for 210 etc.">
                         <QuestionCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                        </Tooltip>
                      }/>
              </Form.Item>

              {!isNewTransactionType?
              <div>
                <Form.Item name="enteredBy" label="Entered By">
                <Space>
                  <span>{data?.enteredBy}</span>
                </Space>
              </Form.Item>
              <Form.Item name="enteredByDate" label="Entered By Date">
                <Space>
                  <span>{ConvertUTCtoCDT(data?.enteredByDate||'')}</span>
                </Space>
              </Form.Item>
              <Form.Item name="updatedBy" label="Updated By">
                <Space>
                  <span>{data?.updatedBy}</span>
                </Space>
              </Form.Item>
              <Form.Item name="updateByDate" label="Updated By Date">
                <Space>
                  <span>{ConvertUTCtoCDT(data?.updatedByDate||'')}</span>
                </Space>
              </Form.Item>      
              </div>:''
              }
              <Form.Item wrapperCol={{ offset: 4, span: 12 }}>
                <Space>
                  <Button type="primary" htmlType="submit" disabled={disabledSave && !isNewTransactionType}>
                    {isNewTransactionType ? 'Add Transaction Type' : 'Save Transaction Type'}
                  </Button>
                  <Button htmlType="button" onClick={handleCancel}>
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Spin>
        </Card>
      </PageHeader>
    </div>
  );
}
